/**
 * Outreach v2 sourcing — API client for the campaign-scoped and entry-scoped
 * routes under /campaigns/{id}/outreach/* and /roster/{entry_id}/outreach/*
 * (discovr-backend internal/outreach, scopes outreach:read/outreach:write).
 * Wire JSON is snake_case like every other endpoint in this codebase; the FE
 * types in @discovr/types are camelCase — the wire shapes and the explicit
 * wire->FE mapping live in ./outreachWire, the same convention
 * importBatchMutations.ts uses. This file is the route surface only.
 *
 * Every mutating call takes idempotencyKey as an explicit parameter rather
 * than minting one inside the call: the caller (a hook) holds it in a ref for
 * the life of one user action, so a double-click or a React Query retry
 * REPLAYS the same request instead of creating a second batch/operation —
 * the BE's Idempotency-Key contract (internal/outreach/idem) depends on the
 * same key being sent for every retry of the same logical action.
 */
import { ApiError, apiClient, auth } from '@discovr/common';
import type { DataResponse, PaginatedResponse } from '@discovr/common';
import type {
  OutreachCounts,
  OutreachEntryEvent,
  OutreachRow,
  OutreachTab,
  OutreachImportBatch,
  OutreachOperation,
  OutreachActiveOperation,
  OutreachBulkSend,
  OutreachFirstTouchDrafts,
  FirstTouchDraftsInput,
  OutreachContactPatch,
  OutreachFillStepRequest,
  OutreachFillStepResult,
  OutreachTemplate,
  OutreachTemplateGroups,
  OutreachTemplateInput,
  OutreachTemplatePatch,
  OutreachTemplatePreview,
  OutreachSettings,
  OutreachSettingsInput,
  OutreachSequenceTemplateCheck,
  OutreachMessage,
  OutreachQueuedMessage,
  OutreachApproveDraftsInput,
  OutreachSendStatus,
  OutreachPoolMailbox,
  OutreachHumanSendInput,
  OutreachRate,
  OutreachSuggestion,
  OutreachCloseReason,
  OutreachDraft,
  OutreachDraftPage,
  OutreachKpis,
  OutreachMailboxHealth,
  OutreachRatesPage,
  OutreachTemplateStepText,
  OutreachHandlePreview,
} from '@discovr/types';
import type {
  WireEntryEvent,
  WireHandlePreview,
  WireImportBatchView,
  WireImportItemView,
  WireMailboxView,
  WireMessage,
  WireOutreachCounts,
  WireActiveOperation,
  WireBulkSend,
  WireFirstTouchDrafts,
  WireOutreachOperation,
  WireOutreachRow,
  WireQueuedMessage,
  WireSendStatus,
  WireSettings,
  WireFillStepResult,
  WireTemplate,
  WireTemplateGroups,
  WireTemplatePreview,
  WireDraft,
  WireKpis,
  WireMailboxHealth,
  WireRatesPage,
  WireSuggestion,
  WireTemplateStepText,
} from './outreachWire';
import { browserTimeZone } from './browserTimeZone';
import {
  fromSettings,
  fromTemplateStep,
  toActiveOperation,
  toBulkSend,
  toFirstTouchDrafts,
  toFillStepResult,
  toDraft,
  toHandlePreview,
  toEntryEvent,
  toKpis,
  toMailboxHealth,
  toMessage,
  toOutreachCounts,
  toOutreachImportBatch,
  toOutreachOperation,
  toOutreachRow,
  toPoolMailbox,
  toQueuedMessage,
  toRatesPage,
  toSendStatus,
  toSettings,
  toSuggestion,
  toTemplateStepText,
  toTemplatePreview,
  toTemplate,
  toWireContactPatch,
} from './outreachWire';

export interface OutreachRowsParams {
  tab: OutreachTab;
  q?: string;
  sort?: 'last_activity' | 'name' | 'status' | 'scheduled_at';
  page: number;
  limit: number;
  reached_from?: string;
  reached_to?: string;
  stage?: string;
  intent?: string;
  locked_rate?: boolean;
}

/** GET /campaigns/{id}/outreach/rates (CRM v2 contract §4); limit is capped at 100 server-side. */
export interface OutreachRatesParams {
  page: number;
  limit: number;
  q?: string;
}

// ── Error mapping ────────────────────────────────────────────────────────
// apiClient's own response interceptor already turns a failed request into an
// ApiError with `.code`/`.message` split apart — but `.message` alone (per
// the interceptor's contract) never repeats the code, so a bare `error.message`
// render would show "used with a different body" with no clue it was
// IDEMPOTENCY_KEY_REUSED. This reshapes the message to lead with the code —
// an ApiError-shaped value (duck-typed, not `instanceof`: this module must
// not hard-depend on the @discovr/common class shape) or a raw axios error
// envelope. Falls back to rethrowing the original error unchanged when
// neither shape matches.
function extractCodeAndMessage(err: unknown): { code: string; message: string } | null {
  if (typeof err !== 'object' || err === null) return null;
  const asApiError = err as { code?: unknown; message?: unknown };
  if (typeof asApiError.code === 'string' && typeof asApiError.message === 'string') {
    return { code: asApiError.code, message: asApiError.message };
  }
  const axiosLike = err as { response?: { data?: { error?: { code?: unknown; message?: unknown } } } };
  const wireError = axiosLike.response?.data?.error;
  if (wireError && typeof wireError.code === 'string') {
    return { code: wireError.code, message: typeof wireError.message === 'string' ? wireError.message : '' };
  }
  return null;
}

// A real ApiError (thrown by apiClient's interceptor in production) already
// has the right prototype chain, `.httpStatus`, `.details`, `.traceId`, and
// `.code` — downstream callers like isNotFoundError() need `instanceof
// ApiError` and `.httpStatus` to survive, not just the string `.code`. So for
// any Error-shaped value we mutate `.message` in place and rethrow the same
// object rather than constructing a new plain Error, which would erase that
// identity. Only a non-Error value (the plain axios-envelope literal this
// module's tests mock) still goes through the synthetic-wrapper fallback.
export function rethrowWithCode(err: unknown): never {
  const shaped = extractCodeAndMessage(err);
  if (!shaped) throw err;
  if (err instanceof Error) {
    err.message = `${shaped.code}: ${shaped.message}`;
    throw err;
  }
  const wrapped = new Error(`${shaped.code}: ${shaped.message}`) as Error & { code: string };
  wrapped.name = 'ApiError';
  wrapped.code = shaped.code;
  throw wrapped;
}

// ── Routes ───────────────────────────────────────────────────────────────

const campaignOutreachPath = (campaignId: string, rest = '') =>
  `/campaigns/${encodeURIComponent(campaignId)}/outreach${rest}`;
export const rosterOutreachPath = (entryId: string, rest = '') =>
  `/roster/${encodeURIComponent(entryId)}/outreach${rest}`;

export function fetchOutreachCounts(campaignId: string): Promise<OutreachCounts> {
  return apiClient
    .get<DataResponse<WireOutreachCounts>>(campaignOutreachPath(campaignId, '/counts'))
    .catch(rethrowWithCode)
    .then((res) => toOutreachCounts(res.data.data));
}

export function fetchOutreachRows(
  campaignId: string,
  params: OutreachRowsParams,
): Promise<{ rows: OutreachRow[]; total: number }> {
  return apiClient
    .get<PaginatedResponse<WireOutreachRow>>(campaignOutreachPath(campaignId, '/creators'), {
      params: {
        tab: params.tab, q: params.q, sort: params.sort, page: params.page, limit: params.limit,
        reached_from: params.reached_from, reached_to: params.reached_to, stage: params.stage,
        intent: params.intent, locked_rate: params.locked_rate,
      },
    })
    .catch(rethrowWithCode)
    .then((res) => ({ rows: res.data.data.map(toOutreachRow), total: res.data.pagination.total }));
}

export function fetchOutreachImport(campaignId: string, importId: number): Promise<OutreachImportBatch> {
  return apiClient
    .get<DataResponse<{ batch: WireImportBatchView; items: WireImportItemView[] }>>(
      campaignOutreachPath(campaignId, `/imports/${importId}`),
    )
    .catch(rethrowWithCode)
    .then((res) => toOutreachImportBatch(res.data.data.batch, res.data.data.items));
}

/** P1-33: re-runs a failed import's failed items; 409 when it isn't failed. */
export function retryOutreachImport(campaignId: string, importId: number): Promise<OutreachImportBatch> {
  return apiClient
    .post<DataResponse<WireImportBatchView>>(campaignOutreachPath(campaignId, `/imports/${importId}/retry`))
    .catch(rethrowWithCode)
    .then((res) => toOutreachImportBatch(res.data.data));
}

export function previewOutreachHandles(campaignId: string, handles: string[]): Promise<OutreachHandlePreview> {
  return apiClient
    .post<DataResponse<WireHandlePreview>>(campaignOutreachPath(campaignId, '/imports/preview'), {
      text: handles.join('\n'),
    })
    .catch(rethrowWithCode)
    .then((res) => toHandlePreview(res.data.data));
}

export function commitOutreachHandles(
  campaignId: string,
  preview: OutreachHandlePreview,
  idempotencyKey: string,
): Promise<{ importId: number }> {
  return apiClient
    .post<DataResponse<{ import_id: number }>>(
      campaignOutreachPath(campaignId, '/imports'),
      preview.commitWithHandles ? { handles: preview.committable } : { file_token: preview.fileToken },
      { headers: { 'Idempotency-Key': idempotencyKey } },
    )
    .catch(rethrowWithCode)
    .then((res) => ({ importId: res.data.data.import_id }));
}

export function patchOutreachContact(
  entryId: string,
  patch: OutreachContactPatch,
  idempotencyKey: string,
): Promise<void> {
  return apiClient
    .patch(rosterOutreachPath(entryId, '/contact'), toWireContactPatch(patch), {
      headers: { 'Idempotency-Key': idempotencyKey },
    })
    .catch(rethrowWithCode)
    .then(() => undefined);
}

export function findContactForEntry(entryId: string, idempotencyKey: string): Promise<{ jobId: string }> {
  return apiClient
    .post<DataResponse<{ job_id: string }>>(
      rosterOutreachPath(entryId, '/find-contact'),
      {},
      { headers: { 'Idempotency-Key': idempotencyKey } },
    )
    .catch(rethrowWithCode)
    .then((res) => ({ jobId: res.data.data.job_id }));
}

export function findContactsForCampaign(
  campaignId: string,
  entryIds: string[] | undefined,
  idempotencyKey: string,
): Promise<{ operationId: string }> {
  return apiClient
    .post<DataResponse<{ operation_id: string }>>(
      campaignOutreachPath(campaignId, '/find-contacts'),
      entryIds && entryIds.length > 0 ? { entry_ids: entryIds } : {},
      { headers: { 'Idempotency-Key': idempotencyKey } },
    )
    .catch(rethrowWithCode)
    .then((res) => ({ operationId: res.data.data.operation_id }));
}

/** F2: the bulk wizard's Step 3 — drafts a first touch for each entry from the batch's template. */
export function startFirstTouchDrafts(
  campaignId: string,
  input: FirstTouchDraftsInput,
  idempotencyKey: string,
): Promise<{ operationId: string }> {
  return apiClient
    .post<DataResponse<{ operation_id: string }>>(
      campaignOutreachPath(campaignId, '/first-touch-drafts'),
      {
        entry_ids: input.entryIds,
        ...(input.templateId !== undefined ? { template_id: input.templateId } : {}),
        ...(input.replaceExisting ? { replace_existing: true } : {}),
        ...(input.includeNoEmail ? { include_no_email: true } : {}),
        ...(input.batchId ? { batch_id: input.batchId } : {}),
      },
      { headers: { 'Idempotency-Key': idempotencyKey } },
    )
    .catch(rethrowWithCode)
    .then((res) => ({ operationId: res.data.data.operation_id }));
}

/** The campaign's Bulk sends not yet sent (progress counts, no creators), for every admin. */
export function fetchOutreachBulkSends(campaignId: string): Promise<OutreachBulkSend[]> {
  return apiClient
    .get<DataResponse<WireBulkSend[] | null>>(campaignOutreachPath(campaignId, '/bulk-sends'))
    .catch(rethrowWithCode)
    .then((res) => (res.data.data ?? []).map(toBulkSend));
}

/** One Bulk send with its creators, to reopen it in the wizard. */
export function fetchOutreachBulkSend(campaignId: string, batchId: string): Promise<OutreachBulkSend> {
  return apiClient
    .get<DataResponse<WireBulkSend>>(campaignOutreachPath(campaignId, `/bulk-sends/${encodeURIComponent(batchId)}`))
    .catch(rethrowWithCode)
    .then((res) => toBulkSend(res.data.data));
}

/** Running operations only, newest first, counts without per-item outcomes. */
export function fetchActiveOutreachOperations(campaignId: string, kind?: string): Promise<OutreachActiveOperation[]> {
  return apiClient
    .get<DataResponse<WireActiveOperation[] | null>>(
      campaignOutreachPath(campaignId, '/operations/active'),
      kind ? { params: { kind } } : undefined,
    )
    .catch(rethrowWithCode)
    .then((res) => (res.data.data ?? []).map(toActiveOperation));
}

export function fetchOutreachOperation(campaignId: string, operationId: string): Promise<OutreachOperation> {
  return apiClient
    .get<DataResponse<WireOutreachOperation>>(
      campaignOutreachPath(campaignId, `/operations/${encodeURIComponent(operationId)}`),
    )
    .catch(rethrowWithCode)
    .then((res) => toOutreachOperation(res.data.data));
}

// ── Templates ────────────────────────────────────────────────────────────
// Template edits go through the CAMPAIGN-scoped paths even for a brand-scoped
// row: the route's {id} is what authorizes the edit, and editing a row other
// campaigns also use needs ?confirm_brand=true. /admin/outreach/templates is
// the separate outreach:manage surface for the global library.

const templatePath = (campaignId: string, templateId: number, rest = '') =>
  campaignOutreachPath(campaignId, `/templates/${templateId}${rest}`);

const confirmBrandConfig = (confirmBrand?: boolean) =>
  confirmBrand ? { params: { confirm_brand: 'true' } } : undefined;

export function fetchOutreachTemplates(campaignId: string): Promise<OutreachTemplateGroups> {
  return apiClient
    .get<DataResponse<WireTemplateGroups>>(campaignOutreachPath(campaignId, '/templates'))
    .catch(rethrowWithCode)
    .then((res) => ({
      global: (res.data.data.global ?? []).map(toTemplate),
      brand: (res.data.data.brand ?? []).map(toTemplate),
      campaign: (res.data.data.campaign ?? []).map(toTemplate),
    }));
}

export function createOutreachTemplate(
  campaignId: string,
  input: OutreachTemplateInput,
): Promise<OutreachTemplate> {
  return apiClient
    .post<DataResponse<WireTemplate>>(campaignOutreachPath(campaignId, '/templates'), {
      scope: input.scope,
      kind: input.kind,
      name: input.name,
      ...(input.nameIsDefault ? { name_is_default: true } : {}),
      steps: input.steps?.map(fromTemplateStep),
      parent_template_id: input.parentTemplateId,
      ...(input.signatureHtml !== undefined ? { signature_html: input.signatureHtml } : { signature: input.signature ?? null }),
    })
    .catch(rethrowWithCode)
    .then((res) => toTemplate(res.data.data));
}

export function patchOutreachTemplate(
  campaignId: string,
  templateId: number,
  patch: OutreachTemplatePatch,
  opts?: { confirmBrand?: boolean },
): Promise<OutreachTemplate> {
  const body: Record<string, unknown> = {};
  if (patch.name !== undefined) body.name = patch.name;
  if (patch.steps !== undefined) body.steps = patch.steps.map(fromTemplateStep);
  if (patch.signatureHtml !== undefined) body.signature_html = patch.signatureHtml;
  else if (patch.signature !== undefined) body.signature = patch.signature;
  if (patch.archiveSteps?.length) body.archive_steps = patch.archiveSteps;
  return apiClient
    .patch<DataResponse<WireTemplate>>(templatePath(campaignId, templateId), body, confirmBrandConfig(opts?.confirmBrand))
    .catch(rethrowWithCode)
    .then((res) => toTemplate(res.data.data));
}

export function fillOutreachTemplateStep(
  campaignId: string,
  req: OutreachFillStepRequest,
): Promise<OutreachFillStepResult> {
  return apiClient
    .post<DataResponse<WireFillStepResult>>(campaignOutreachPath(campaignId, '/templates/fill-step'), {
      step_no: req.stepNo,
      steps_total: req.stepsTotal,
      ...(req.subject ? { subject: req.subject } : {}),
      ...(req.bodyHtml ? { body_html: req.bodyHtml } : {}),
      ...(req.instruction ? { instruction: req.instruction } : {}),
      ...(req.delayDays !== undefined ? { delay_days: req.delayDays } : {}),
      prior_steps: req.priorSteps.map((p) => ({ ...(p.subject ? { subject: p.subject } : {}), body_html: p.bodyHtml })),
    })
    .catch(rethrowWithCode)
    .then((res) => toFillStepResult(res.data.data));
}

export function archiveOutreachTemplate(
  campaignId: string,
  templateId: number,
  opts?: { confirmBrand?: boolean },
): Promise<void> {
  return apiClient
    .delete(templatePath(campaignId, templateId), confirmBrandConfig(opts?.confirmBrand))
    .catch(rethrowWithCode)
    .then(() => undefined);
}

export function restoreOutreachTemplate(
  campaignId: string,
  templateId: number,
  opts?: { confirmBrand?: boolean },
): Promise<void> {
  return apiClient
    .post(templatePath(campaignId, templateId, '/restore'), undefined, confirmBrandConfig(opts?.confirmBrand))
    .catch(rethrowWithCode)
    .then(() => undefined);
}

export function previewOutreachTemplate(
  campaignId: string,
  templateId: number,
  entryId: string,
): Promise<OutreachTemplatePreview> {
  return apiClient
    .post<DataResponse<WireTemplatePreview>>(templatePath(campaignId, templateId, '/preview'), { entry_id: entryId })
    .catch(rethrowWithCode)
    .then((res) => toTemplatePreview(res.data.data));
}

// ── Settings, pool and send status ───────────────────────────────────────

export function fetchOutreachSettings(campaignId: string): Promise<OutreachSettings> {
  return apiClient
    .get<DataResponse<WireSettings>>(campaignOutreachPath(campaignId, '/settings'))
    .catch(rethrowWithCode)
    .then((res) => toSettings(res.data.data));
}

export function putOutreachSettings(campaignId: string, input: OutreachSettingsInput): Promise<OutreachSettings> {
  return apiClient
    .put<DataResponse<WireSettings>>(campaignOutreachPath(campaignId, '/settings'), fromSettings(input))
    .catch(rethrowWithCode)
    .then((res) => toSettings(res.data.data));
}

/** Task 6 / P8-6: the template picker's own PUT on the same settings route —
 *  `dry: true` checks how many waiting drafts a template change would
 *  regenerate with no side effects; `dry: false` commits it. Go's PUT
 *  /campaigns/{id}/outreach/settings is a true full-replace
 *  (settings.Service.Put writes every field via UpsertCampaignConfig, no
 *  partial-patch merging) — ONLY the dry check skips every field but
 *  sequence_template_id. So the commit (`dry: false`) must carry the
 *  campaign's full current settings merged with the new template id, or
 *  every other field (zone, caps, ...) comes through
 *  as Go's zero value and gets written as such. `currentSettings` is
 *  required whenever `dry` is false.
 *
 *  On a commit, Go answers the full settings View (same shape
 *  `putOutreachSettings` gets back) alongside `regenerates` — parsed here
 *  via the same `toSettings` and returned as `settings`, so the caller can
 *  reseed its cache with the fresh `updatedAt` the write actually bumped.
 *  Narrowing this down to `{regenerates}` alone (as the dry check
 *  genuinely is) would leave the cache's `updatedAt` stale and false-412
 *  the very next write in the same session. */
export async function putOutreachSequenceTemplate(
  campaignId: string,
  sequenceTemplateId: number | null,
  dry: boolean,
  currentSettings?: OutreachSettingsInput,
): Promise<OutreachSequenceTemplateCheck> {
  // async, not a plain Promise chain: commitSequenceTemplateBody's guard
  // throw must reject the returned promise, not throw synchronously out of
  // this call (which would bypass every caller's `.catch`/`await` handling).
  const body = dry
    ? { sequence_template_id: sequenceTemplateId, dry: true }
    : commitSequenceTemplateBody(sequenceTemplateId, currentSettings);
  const res = await apiClient
    .put<DataResponse<WireSettings & { regenerates: number }>>(campaignOutreachPath(campaignId, '/settings'), body)
    .catch(rethrowWithCode);
  const regenerates = res.data.data.regenerates;
  return dry ? { regenerates } : { regenerates, settings: toSettings(res.data.data) };
}

function commitSequenceTemplateBody(
  sequenceTemplateId: number | null,
  currentSettings: OutreachSettingsInput | undefined,
): Record<string, unknown> {
  if (!currentSettings) {
    throw new Error('A template change commit requires the campaign\'s current settings');
  }
  return { ...fromSettings({ ...currentSettings, sequenceTemplateId: sequenceTemplateId ?? undefined }), dry: false };
}

export function pauseOutreach(campaignId: string): Promise<OutreachSettings> {
  return apiClient
    .post<DataResponse<WireSettings>>(campaignOutreachPath(campaignId, '/settings/pause'), {})
    .catch(rethrowWithCode)
    .then((res) => toSettings(res.data.data));
}

export function resumeOutreach(campaignId: string): Promise<OutreachSettings> {
  return apiClient
    .post<DataResponse<WireSettings>>(campaignOutreachPath(campaignId, '/settings/resume'), {})
    .catch(rethrowWithCode)
    .then((res) => toSettings(res.data.data));
}

/** The campaign-scoped (outreach:read) pool read — NOT /admin/outreach/pool,
 *  which is outreach:manage and invisible to an admin_member. */
export function fetchOutreachPool(campaignId: string): Promise<OutreachPoolMailbox[]> {
  return apiClient
    .get<DataResponse<WireMailboxView[] | null>>(campaignOutreachPath(campaignId, '/mailboxes'))
    .catch(rethrowWithCode)
    .then((res) => (res.data.data ?? []).map(toPoolMailbox));
}

export function fetchOutreachStatus(): Promise<OutreachSendStatus> {
  return apiClient
    .get<DataResponse<WireSendStatus>>('/outreach/status')
    .catch(rethrowWithCode)
    .then((res) => toSendStatus(res.data.data));
}

// ── Sending and the entry thread ─────────────────────────────────────────

const keyed = (idempotencyKey: string) => ({ headers: { 'Idempotency-Key': idempotencyKey } });

const messagePath = (entryId: string, messageId: number, action: string) =>
  rosterOutreachPath(entryId, `/messages/${messageId}/${action}`);

/** Message step: request a first-touch draft for any of these entries that lacks one. Idempotent per entry (a repeat answers `exists`). */
export function ensureDrafts(campaignId: string, entryIds: string[], idempotencyKey: string): Promise<{ operationId: string }> {
  return apiClient
    .post<DataResponse<{ operation_id: string }>>(
      campaignOutreachPath(campaignId, '/drafts/ensure'),
      { entry_ids: entryIds },
      keyed(idempotencyKey),
    )
    .catch(rethrowWithCode)
    .then((res) => ({ operationId: res.data.data.operation_id }));
}

/** Oldest first — a thread reads top-down, so page 1 is the conversation's start. */
export function fetchOutreachThread(
  entryId: string,
  page: number,
  limit: number,
): Promise<{ rows: OutreachMessage[]; total: number }> {
  return apiClient
    .get<PaginatedResponse<WireMessage>>(rosterOutreachPath(entryId, '/messages'), { params: { page, limit } })
    .catch(rethrowWithCode)
    .then((res) => ({ rows: res.data.data.map(toMessage), total: res.data.pagination.total }));
}

/** The entry's admin actions, oldest first (E9-93). */
export function fetchOutreachEntryEvents(entryId: string): Promise<OutreachEntryEvent[]> {
  return apiClient
    .get<DataResponse<WireEntryEvent[] | null>>(rosterOutreachPath(entryId, '/events'))
    .catch(rethrowWithCode)
    .then((res) => (res.data.data ?? []).map(toEntryEvent));
}

export function fetchOutreachMessage(entryId: string, messageId: number): Promise<OutreachMessage> {
  return apiClient
    .get<DataResponse<WireMessage>>(rosterOutreachPath(entryId, `/messages/${messageId}`))
    .catch(rethrowWithCode)
    .then((res) => toMessage(res.data.data));
}

/** A held message's "send anyway": override_cooldown releases a recent-contact hold,
 *  override_address_guard a no_mx / role_address / disposable_address hold (and a flagged draft on Send now).
 *  Go requires the address the operator saw with the address override and refuses it for any other (discovr #749). */
export interface SendOverrideOptions {
  overrideCooldown?: boolean;
  addressGuard?: { expectedTo: string };
}

function overrideBody(opts: SendOverrideOptions): Record<string, true | string> {
  return {
    ...(opts.overrideCooldown ? { override_cooldown: true as const } : {}),
    ...(opts.addressGuard ? { override_address_guard: true as const, expected_to: opts.addressGuard.expectedTo } : {}),
  };
}

/** Z1: Send now runs in the admin's zone, so the browser's zone rides along (omitted when none is detected). */
function adminTzBody(): { admin_tz?: string } {
  const adminTz = browserTimeZone();
  return adminTz ? { admin_tz: adminTz } : {};
}

export function resendOutreachMessage(
  entryId: string,
  messageId: number,
  idempotencyKey: string,
  opts: SendOverrideOptions = {},
): Promise<OutreachQueuedMessage> {
  return apiClient
    .post<DataResponse<WireQueuedMessage>>(
      messagePath(entryId, messageId, 'resend'),
      overrideBody(opts),
      { headers: { 'Idempotency-Key': idempotencyKey } },
    )
    .catch(rethrowWithCode)
    .then((res) => toQueuedMessage(res.data.data));
}

/** Recover one outbound message in place. Unlike the legacy resend route,
 * this never creates a replacement thread row. */
export function sendNowOutreachMessage(
  entryId: string,
  messageId: number,
  idempotencyKey: string,
  opts: SendOverrideOptions = {},
): Promise<OutreachQueuedMessage> {
  return apiClient
    .post<DataResponse<WireQueuedMessage>>(
      messagePath(entryId, messageId, 'send-now'),
      { ...overrideBody(opts), ...adminTzBody() },
      keyed(idempotencyKey),
    )
    .catch(rethrowWithCode)
    .then((res) => toQueuedMessage(res.data.data));
}

export function markOutreachMessageSent(
  entryId: string,
  messageId: number,
  reason: string,
  idempotencyKey: string,
): Promise<OutreachMessage> {
  return apiClient
    .post<DataResponse<WireMessage>>(messagePath(entryId, messageId, 'mark-sent'), { reason }, {
      headers: { 'Idempotency-Key': idempotencyKey },
    })
    .catch(rethrowWithCode)
    .then((res) => toMessage(res.data.data));
}

/** No Idempotency-Key by design: cancelling twice is cancelling once, and the
 *  second call answers 200 with the already-cancelled row. */
export function cancelOutreachMessage(entryId: string, messageId: number): Promise<OutreachMessage> {
  return apiClient
    .post<DataResponse<WireMessage>>(messagePath(entryId, messageId, 'cancel'), {})
    .catch(rethrowWithCode)
    .then((res) => toMessage(res.data.data));
}

/** The creator's first-touch drafts, newest first (reading never writes one). */
export function fetchFirstTouchDrafts(entryId: string): Promise<OutreachFirstTouchDrafts> {
  return apiClient
    .get<DataResponse<WireFirstTouchDrafts>>(rosterOutreachPath(entryId, '/first-touch-drafts'))
    .catch(rethrowWithCode)
    .then((res) => toFirstTouchDrafts(res.data.data));
}

/** Makes an earlier first-touch draft the current one: the one Send uses. */
export function selectFirstTouchDraft(entryId: string, messageId: number, idempotencyKey: string): Promise<OutreachFirstTouchDrafts> {
  return apiClient
    .post<DataResponse<WireFirstTouchDrafts>>(rosterOutreachPath(entryId, `/first-touch-drafts/${messageId}/select`), undefined, keyed(idempotencyKey))
    .catch(rethrowWithCode)
    .then((res) => toFirstTouchDrafts(res.data.data));
}

/** E9-54: cancels the first-touch draft awaiting approval (admin); a fresh draft is written the next time the wizard runs. */
export function discardOutreachDraft(entryId: string, messageId: number, idempotencyKey: string): Promise<OutreachMessage> {
  return apiClient
    .delete<DataResponse<WireMessage>>(messagePath(entryId, messageId, 'draft'), keyed(idempotencyKey))
    .catch(rethrowWithCode)
    .then((res) => toMessage(res.data.data));
}

// ── 2.5: human sends, negotiation, entry actions ─────────────────────────

export interface OutreachHumanSendResult {
  messageId: number;
  status: string;
  /** Why the message is held, when status is held. */
  heldReason?: string;
  scheduledFor?: string;
  round?: number;
  agreed: boolean;
  /** The human-visible round count; there is no round limit. */
  humanRound?: number;
  totalRound?: number;
}

/** SendMessage's kind=reply|confirm|counter path. A first touch is refused by Go (410 FIRST_TOUCH_WIZARD_ONLY). */
export function sendOutreachMessage(
  entryId: string,
  input: OutreachHumanSendInput,
  idempotencyKey: string,
): Promise<OutreachHumanSendResult> {
  return apiClient
    .post<
      DataResponse<{
        message_id: number;
        status: string;
        held_reason?: string;
        scheduled_for?: string;
        round?: number;
        agreed?: boolean;
        human_round?: number;
        total_round?: number;
      }>
    >(
      rosterOutreachPath(entryId, '/messages'),
      {
        kind: input.kind,
        subject: input.subject,
        body: input.body,
        body_html: input.bodyHtml,
        rates: input.rates,
        scheduled_for: input.scheduledFor,
        suggestion_job_id: input.suggestionJobId,
        clear_composer_draft: input.clearComposerDraft,
        followup: input.followup,
        from_template_step: input.fromTemplateStep,
      },
      { headers: { 'Idempotency-Key': idempotencyKey } },
    )
    .catch(rethrowWithCode)
    .then((res) => ({
      messageId: res.data.data.message_id,
      status: res.data.data.status,
      heldReason: res.data.data.held_reason,
      scheduledFor: res.data.data.scheduled_for,
      round: res.data.data.round,
      agreed: res.data.data.agreed ?? false,
      humanRound: res.data.data.human_round,
      totalRound: res.data.data.total_round,
    }));
}

export interface HelpMeWriteInput {
  prompt: string;
  kind: 'reply' | 'confirm' | 'counter';
  /** Counter only: the amounts a human typed. The route never invents one. */
  rates?: OutreachRate[];
}

export interface HelpMeWriteStreamOptions {
  signal?: AbortSignal;
  onToken?: (text: string) => void;
}

export const HELP_ME_WRITE_INTERRUPTED = 'HELP_ME_WRITE_INTERRUPTED';

async function helpMeWriteHttpError(res: Response): Promise<ApiError> {
  let env: { error?: { code?: string; message?: string; details?: unknown }; trace_id?: string } = {};
  try {
    env = (await res.json()) as typeof env;
  } catch {
    /* not a JSON body */
  }
  return new ApiError(env.error?.code ?? 'UNKNOWN', env.error?.message ?? `help-me-write failed: ${res.status}`, res.status, env.trace_id, env.error?.details);
}

/** POST .../threads/{entry}/help-me-write, an SSE stream: `token` chunks, then exactly one `done` with the full
 *  text, or one `error`. Resolves with the done text only; an error event, a dropped stream or an abort throws, so the
 *  caller discards whatever tokens it showed. */
export async function helpMeWriteOutreachReply(
  campaignId: string,
  entryId: string,
  input: HelpMeWriteInput,
  opts: HelpMeWriteStreamOptions = {},
): Promise<OutreachSuggestion> {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`${apiClient.defaults.baseURL}${campaignOutreachPath(campaignId, `/threads/${encodeURIComponent(entryId)}/help-me-write`)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ prompt: input.prompt, kind: input.kind }),
    signal: opts.signal,
  });
  if (!res.ok || !res.body) throw await helpMeWriteHttpError(res);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let done: string | null = null;
  const consume = (block: string) => {
    let event = '';
    const data: string[] = [];
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) data.push(line.slice(5).replace(/^ /, ''));
    }
    let payload: { text?: string; code?: string; reason?: string; retryable?: boolean } = {};
    try {
      payload = JSON.parse(data.join('\n')) as typeof payload;
    } catch {
      /* malformed event: treated as no payload */
    }
    if (event === 'token') opts.onToken?.(payload.text ?? '');
    else if (event === 'done') done = payload.text ?? '';
    else if (event === 'error') throw new ApiError('AI_COMPOSE_FAILED', 'help-me-write failed', 200, undefined, { reason: payload.reason ?? payload.code ?? '', retryable: payload.retryable ?? true });
  };
  try {
    while (done === null) {
      const { value, done: eof } = await reader.read();
      if (eof) break;
      buffer = (buffer + decoder.decode(value, { stream: true })).replace(/\r/g, '');
      const blocks = buffer.split('\n\n');
      buffer = blocks.pop() ?? '';
      for (const block of blocks) {
        if (block) consume(block);
        if (done !== null) break;
      }
    }
    if (done === null && buffer.trim()) consume(buffer);
  } finally {
    void reader.cancel().catch(() => undefined);
  }
  if (done === null) throw new ApiError(HELP_ME_WRITE_INTERRUPTED, 'help-me-write stream ended early', 200);
  return { subject: '', body: done };
}

/** Review fix (HIGH — Ruling 23 / G9): the agree-rate route carries the same
 *  round fields as counter/confirm so AgreeRateDialog can show "Round n" too. */
export function agreeOutreachRate(
  entryId: string,
  rates: OutreachRate[],
  idempotencyKey: string,
): Promise<{ round: number; humanRound?: number; totalRound?: number }> {
  return apiClient
    .post<DataResponse<{ round: number; human_round?: number; total_round?: number }>>(
      rosterOutreachPath(entryId, '/agree-rate'),
      {
        rates,
      },
      keyed(idempotencyKey),
    )
    .catch(rethrowWithCode)
    .then((res) => ({
      round: res.data.data.round,
      humanRound: res.data.data.human_round,
      totalRound: res.data.data.total_round,
    }));
}

/** The BrandStrip's final-confirmation prefill: the only remaining caller of suggest-reply (kind confirm). No key: a draft is not a fact. */
export function fetchConfirmationDraft(entryId: string, rates: OutreachRate[]): Promise<OutreachSuggestion> {
  return apiClient
    .post<DataResponse<WireSuggestion>>(rosterOutreachPath(entryId, '/suggest-reply'), { kind: 'confirm', rates })
    .catch(rethrowWithCode)
    .then((res) => toSuggestion(res.data.data));
}

/** No key: a draft is not a fact. */
export function fetchCounterDraft(entryId: string, rates: OutreachRate[]): Promise<OutreachSuggestion> {
  return apiClient
    .post<DataResponse<WireSuggestion>>(rosterOutreachPath(entryId, '/suggest-reply'), { kind: 'counter', rates })
    .catch(rethrowWithCode)
    .then((res) => toSuggestion(res.data.data));
}

/** No due gate; never sends, no key. */
export function fetchNudgeDraft(entryId: string): Promise<OutreachSuggestion> {
  return apiClient
    .post<DataResponse<WireSuggestion>>(rosterOutreachPath(entryId, '/suggest-reply'), { kind: 'nudge' })
    .catch(rethrowWithCode)
    .then((res) => toSuggestion(res.data.data));
}

/** 404 NO_SUGGESTION carries details.reason when the AI wrote none. No key. */
export function fetchReplySuggestion(entryId: string): Promise<OutreachSuggestion> {
  return apiClient
    .post<DataResponse<WireSuggestion>>(rosterOutreachPath(entryId, '/suggest-reply'), { kind: 'reply' })
    .catch(rethrowWithCode)
    .then((res) => toSuggestion(res.data.data));
}

/** A creator with no frozen sequence yet reads step 1 from the failed batch's template (templateId). */
export function fetchTemplateStepText(entryId: string, step: number, templateId?: number): Promise<OutreachTemplateStepText> {
  return apiClient
    .get<DataResponse<WireTemplateStepText>>(rosterOutreachPath(entryId, '/template-step'), {
      params: { step, ...(templateId !== undefined ? { template_id: templateId } : {}) },
    })
    .catch(rethrowWithCode)
    .then((res) => toTemplateStepText(res.data.data));
}

export interface FirstTouchByHandInput {
  subject: string;
  body: string;
  bodyHtml?: string;
  /** The batch's template: supplies the frozen sequence the follow-ups use. */
  templateId?: number;
  /** The text started as step 1 (the Template button): the draft is stamped draft_source template. */
  fromTemplate?: boolean;
}

/** Parks a first-touch draft written by hand (or from the Template button) for approval; never sends. */
export function writeFirstTouchByHand(entryId: string, input: FirstTouchByHandInput, idempotencyKey: string): Promise<void> {
  const adminTz = browserTimeZone();
  return apiClient
    .post(
      rosterOutreachPath(entryId, '/first-touch-by-hand'),
      {
        subject: input.subject,
        body: input.body,
        ...(input.bodyHtml ? { body_html: input.bodyHtml } : {}),
        ...(input.templateId !== undefined ? { template_id: input.templateId } : {}),
        ...(input.fromTemplate ? { from_template: true } : {}),
        ...(adminTz ? { admin_tz: adminTz } : {}),
      },
      { headers: { 'Idempotency-Key': idempotencyKey } },
    )
    .catch(rethrowWithCode)
    .then(() => undefined);
}

/** The composer's "Follow up" pill (Go #706): an agent-written follow-up once one is due. 409 NO_FOLLOWUP_DUE when none
 *  is; never sends, never stores a draft, so no key. */
export function fetchFollowupDraft(entryId: string): Promise<OutreachSuggestion> {
  return apiClient
    .post<DataResponse<WireSuggestion>>(rosterOutreachPath(entryId, '/suggest-reply'), { kind: 'followup' })
    .catch(rethrowWithCode)
    .then((res) => toSuggestion(res.data.data));
}

/** No key: a draft is not a fact. */
export function fetchAcknowledgementDraft(entryId: string): Promise<OutreachSuggestion> {
  return apiClient
    .get<DataResponse<WireSuggestion>>(rosterOutreachPath(entryId, '/acknowledgement-draft'))
    .catch(rethrowWithCode)
    .then((res) => toSuggestion(res.data.data));
}

/** The bulk "Move to brand dashboard": one durable `send_to_brand` operation, up to 200 entries. */
export function sendOutreachToBrandMany(
  campaignId: string,
  entryIds: string[],
  idempotencyKey: string,
): Promise<{ operationId: string }> {
  return apiClient
    .post<DataResponse<{ operation_id: string }>>(
      campaignOutreachPath(campaignId, '/send-to-brand'),
      { entry_ids: entryIds },
      keyed(idempotencyKey),
    )
    .catch(rethrowWithCode)
    .then((res) => ({ operationId: res.data.data.operation_id }));
}

/** Admin records what the brand said in the roster chat (`brand_counter_requested`). */
export function recordOutreachBrandCounter(entryId: string, input: { note?: string }, idempotencyKey: string): Promise<void> {
  return apiClient
    .post(rosterOutreachPath(entryId, '/brand-counter'), { note: input.note }, keyed(idempotencyKey))
    .catch(rethrowWithCode)
    .then(() => undefined);
}

/** Admin withdraws the recorded "brand asked for less" note and its flag; writes no price. */
export function clearOutreachBrandCounter(entryId: string, idempotencyKey: string): Promise<void> {
  return apiClient
    .post(rosterOutreachPath(entryId, '/brand-counter/clear'), {}, keyed(idempotencyKey))
    .catch(rethrowWithCode)
    .then(() => undefined);
}

/** Admin dismisses an unanswered creator reply that needs no answer, until the next reply. */
export function outreachNoReplyNeeded(entryId: string, input: { note?: string }, idempotencyKey: string): Promise<void> {
  return apiClient
    .post(rosterOutreachPath(entryId, '/no-reply-needed'), { note: input.note }, keyed(idempotencyKey))
    .catch(rethrowWithCode)
    .then(() => undefined);
}

/** The shared shape of every entry action whose response the caller only
 *  needs as a refresh signal (the row/thread invalidation carries the real
 *  update) — sendToBrand/close/reopen/pauseFollowups/remove all answer
 *  `{cancelled_messages}`, which nothing downstream reads today. */
const entryAction =
  (suffix: string) =>
  (entryId: string, idempotencyKey: string, body: Record<string, unknown> = {}) =>
    apiClient
      .post<DataResponse<{ cancelled_messages?: number }>>(rosterOutreachPath(entryId, suffix), body, keyed(idempotencyKey))
      .catch(rethrowWithCode)
      .then(() => undefined);

/** An open Needs you flag is cleared by the hand-off; Go answers 409 OPEN_FLAG_CONFIRM until the admin confirms (G8-1). */
export const sendOutreachToBrand = (entryId: string, key: string, confirmOpenFlag = false) =>
  entryAction('/send-to-brand')(entryId, key, confirmOpenFlag ? { confirm_open_flag: true } : {});
export const closeOutreach = (entryId: string, reason: OutreachCloseReason, key: string) =>
  entryAction('/close')(entryId, key, { reason });
export const reopenOutreach = (entryId: string, key: string) => entryAction('/reopen')(entryId, key);
/** R10-4: lifts the unsubscribe the agent read in a reply, then reopens (or, with the brand, only clears Needs you). */
export const notUnsubscribeOutreach = (entryId: string, key: string) => entryAction('/not-unsubscribe')(entryId, key);
export const pauseOutreachFollowups = (entryId: string, key: string) => entryAction('/followups/pause')(entryId, key);
/** R17-67: the follow-up that fell due while paused. Go answers 409 NO_LAPSED_FOLLOWUP once it is already handled. */
export const sendLapsedFollowupNow = (entryId: string, key: string) => entryAction('/followups/send-now')(entryId, key);
export const skipLapsedFollowup = (entryId: string, key: string) => entryAction('/followups/skip')(entryId, key);
export const removeOutreachEntry = (entryId: string, reason: string, key: string) =>
  entryAction('/remove')(entryId, key, { reason });

/** Scope 3: only a human clears an escalation (agent or rule); writes escalation_cleared. */
export function clearOutreachEscalation(entryId: string, input: { note?: string }, idempotencyKey: string): Promise<{ cleared: boolean }> {
  return apiClient
    .post<DataResponse<{ cleared: boolean }>>(rosterOutreachPath(entryId, '/escalation/clear'), { note: input.note }, keyed(idempotencyKey))
    .catch(rethrowWithCode)
    .then((res) => ({ cleared: res.data.data.cleared }));
}

/** Go answers `{}` when Resume only lifts the pause and no step was scheduled. */
export interface ResumeFollowupsResult {
  step?: number;
  scheduledAt?: string;
}

export function resumeOutreachFollowups(entryId: string, idempotencyKey: string): Promise<ResumeFollowupsResult> {
  return apiClient
    .post<DataResponse<{ step?: number; scheduled_at?: string }>>(rosterOutreachPath(entryId, '/followups/resume'), {}, keyed(idempotencyKey))
    .catch(rethrowWithCode)
    .then((res) => ({ step: res.data.data?.step, scheduledAt: res.data.data?.scheduled_at }));
}

/** No Idempotency-Key (ruling 50): idempotent by nature — the BE skips the
 *  write when the fold already agrees, so a retry writes at most one fact. */
export const markOutreachRead = (entryId: string) =>
  apiClient
    .post<DataResponse<{ changed: boolean }>>(rosterOutreachPath(entryId, '/read'))
    .catch(rethrowWithCode)
    .then((r) => r.data.data.changed);
export const markOutreachUnread = (entryId: string) =>
  apiClient
    .post<DataResponse<{ changed: boolean }>>(rosterOutreachPath(entryId, '/unread'))
    .catch(rethrowWithCode)
    .then((r) => r.data.data.changed);

export function removeOutreachEntries(
  campaignId: string,
  entryIds: string[],
  reason: string,
  idempotencyKey: string,
): Promise<{ operationId: string }> {
  return apiClient
    .post<DataResponse<{ operation_id: string }>>(
      campaignOutreachPath(campaignId, '/remove'),
      { entry_ids: entryIds, reason },
      keyed(idempotencyKey),
    )
    .catch(rethrowWithCode)
    .then((res) => ({ operationId: res.data.data.operation_id }));
}

/** 2.10 C2 / Go A5: bulk retry over the existing resend — a durable
 *  `resend_messages` operation. Go caps ids at 200; the outreach page-size cap
 *  (100) keeps a page's selection under it. */
export function resendOutreachMessages(
  campaignId: string,
  messageIds: number[],
  idempotencyKey: string,
): Promise<{ operationId: string }> {
  return apiClient
    .post<DataResponse<{ operation_id: string }>>(
      campaignOutreachPath(campaignId, '/resend'),
      { message_ids: messageIds },
      keyed(idempotencyKey),
    )
    .catch(rethrowWithCode)
    .then((res) => ({ operationId: res.data.data.operation_id }));
}

// ── 2.5: drafts ──────────────────────────────────────────────────────────

export interface OutreachDraftsParams {
  kind?: 'initial' | 'followup' | 'all';
  page?: number;
  limit?: number;
}

export function fetchOutreachDrafts(campaignId: string, params: OutreachDraftsParams = {}): Promise<OutreachDraftPage> {
  const page = params.page ?? 1;
  const limit = params.limit ?? 25;
  const kind = params.kind ? `&kind=${params.kind}` : '';
  return apiClient
    .get<PaginatedResponse<WireDraft>>(campaignOutreachPath(campaignId, `/drafts?page=${page}&limit=${limit}${kind}`))
    .catch(rethrowWithCode)
    .then((res) => ({ items: res.data.data.map(toDraft), total: res.data.pagination.total, page, limit }));
}

/** A draft edit: the text, and the editor's formatting for it (the server derives the text from the formatting). */
export interface OutreachDraftEdit {
  subject?: string;
  body?: string;
  bodyHtml?: string;
}

/** Saves an edit to a draft's subject/body without approving it: nothing is queued to send.
 *  The handler (internal/outreach/handler_drafts.go's Edit) returns a flat
 *  sending.MessageView, not the {message, entry} WireDraft shape used by the
 *  drafts-queue LIST endpoint. */
export function patchOutreachDraft(
  entryId: string,
  messageId: number,
  edit: OutreachDraftEdit,
  idempotencyKey: string,
): Promise<OutreachMessage> {
  return apiClient
    .patch<DataResponse<WireMessage>>(
      rosterOutreachPath(entryId, `/messages/${messageId}/draft`),
      { subject: edit.subject, body: edit.body, body_html: edit.bodyHtml },
      keyed(idempotencyKey),
    )
    .catch(rethrowWithCode)
    .then((res) => toMessage(res.data.data));
}

export function approveOutreachDrafts(campaignId: string, input: OutreachApproveDraftsInput, idempotencyKey: string): Promise<{ operationId: string }> {
  return apiClient
    .post<DataResponse<{ operation_id: string }>>(
      campaignOutreachPath(campaignId, '/messages/approve'),
      { message_ids: input.messageIds, mailbox_id: input.mailboxId, scheduled_for: input.scheduledFor, send_now: input.sendNow, wizard_batch_id: input.wizardBatchId, ...adminTzBody() },
      keyed(idempotencyKey),
    )
    .catch(rethrowWithCode)
    .then((res) => ({ operationId: res.data.data.operation_id }));
}

// ── 2.5: single row and KPIs ────────────────────────────────────────────

/** ruling 60: 2.2's Detail route already returns the full RowView — a 404
 *  maps to null (the entry left the page's filtered set, or was never
 *  loaded), every other error rethrows. */
export function fetchOutreachRow(entryId: string): Promise<OutreachRow | null> {
  return apiClient
    .get<DataResponse<WireOutreachRow>>(rosterOutreachPath(entryId, ''))
    .then((res) => toOutreachRow(res.data.data))
    .catch((e: unknown) => {
      if (typeof e === 'object' && e !== null && (e as { response?: { status?: number } }).response?.status === 404) return null;
      return rethrowWithCode(e);
    });
}

/** Asks Go to skip its per-campaign KPI cache: only for a read right after the caller's own write. */
const FRESH_KPIS_PARAMS = { fresh: true } as const;

function getOutreachKpis(campaignId: string, params?: typeof FRESH_KPIS_PARAMS): Promise<OutreachKpis> {
  return apiClient
    .get<DataResponse<WireKpis>>(campaignOutreachPath(campaignId, '/kpis'), { params })
    .catch(rethrowWithCode)
    .then((res) => toKpis(res.data.data));
}

export function fetchOutreachKpis(campaignId: string): Promise<OutreachKpis> {
  return getOutreachKpis(campaignId);
}

/** The campaign's KPIs as they are now, past Go's cache, so the caller sees its own write. */
export function fetchFreshOutreachKpis(campaignId: string): Promise<OutreachKpis> {
  return getOutreachKpis(campaignId, FRESH_KPIS_PARAMS);
}

// ── CRM v2: mailbox health and rates ─────────────────────────────────────

export function fetchOutreachMailboxHealth(campaignId: string): Promise<OutreachMailboxHealth> {
  return apiClient
    .get<DataResponse<WireMailboxHealth>>(campaignOutreachPath(campaignId, '/mailbox-health'))
    .catch(rethrowWithCode)
    .then((res) => toMailboxHealth(res.data.data));
}

export function fetchOutreachRates(campaignId: string, params: OutreachRatesParams): Promise<OutreachRatesPage> {
  return apiClient
    .get<DataResponse<WireRatesPage>>(campaignOutreachPath(campaignId, '/rates'), {
      params: { page: params.page, limit: params.limit, q: params.q },
    })
    .catch(rethrowWithCode)
    .then((res) => toRatesPage(res.data.data));
}
